import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, TrendingUp, Users, Ticket, PauseCircle, ShieldAlert, Percent, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { toast } from "sonner";

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/superadmin/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => {
        // Silent: role guard already redirects non-super_admin. Avoid duplicate toasts.
        if (e.response?.status !== 403) toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setErr(e.response?.data?.detail || "Impossible de charger le tableau de bord.");
      });
  }, []);
  if (err) return <div className="text-red-500 text-sm">{String(err)}</div>;
  if (!data) return <div className="text-slate-500">Chargement…</div>;

  // Guard against missing fields — the backend must never crash the SA console on null values
  const monthlyRevenue = Array.isArray(data.monthly_revenue) ? data.monthly_revenue : [];
  const byPlan = Array.isArray(data.by_plan) ? data.by_plan : [];
  const num = (v) => (typeof v === "number" ? v : 0);

  const stats = [
    { label: "Entreprises", value: num(data.companies_total), icon: Building2, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-500/10" },
    { label: "Actives", value: num(data.companies_active), icon: Sparkles, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
    { label: "Essai", value: num(data.companies_trial), icon: TrendingUp, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-500/10" },
    { label: "Suspendues", value: num(data.companies_suspended), icon: PauseCircle, color: "text-red-500", bg: "bg-red-50 dark:bg-red-500/10" },
    { label: "Expirées", value: num(data.companies_expired), icon: ShieldAlert, color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-500/10" },
    { label: "Utilisateurs", value: num(data.users_total), icon: Users, color: "text-purple-500", bg: "bg-purple-50 dark:bg-purple-500/10" },
    { label: "Billets vendus", value: num(data.tickets_total), icon: Ticket, color: "text-orange-500", bg: "bg-orange-50 dark:bg-orange-500/10" },
    { label: "Conversion FREE→PAID", value: `${num(data.conversion_free_to_paid)}%`, icon: Percent, color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-500/10", isString: true },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-orange-500 font-semibold">SaaS Owner</div>
        <h1 className="font-display text-3xl font-bold mt-1">Dashboard SaaS</h1>
        <p className="text-sm text-slate-500">Vue d'ensemble de la plateforme Transport.pro</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} data-testid={`sa-stat-${s.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.label}</div>
                <div className={`w-8 h-8 rounded-lg grid place-items-center ${s.bg}`}><s.icon className={`w-4 h-4 ${s.color}`} /></div>
              </div>
              <div className="stat-num mt-2">{s.isString ? s.value : s.value.toLocaleString("fr-FR")}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-lg">Revenus SaaS mensuels</CardTitle></CardHeader>
          <CardContent className="h-64">
            {monthlyRevenue.length ? (
              <ResponsiveContainer>
                <LineChart data={monthlyRevenue}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={3} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-sm text-slate-500">Pas encore de paiement SaaS enregistré</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Répartition par plan</CardTitle></CardHeader>
          <CardContent className="h-64">
            {byPlan.length ? (
              <ResponsiveContainer>
                <BarChart data={byPlan}>
                  <XAxis dataKey="plan" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#F97316" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-sm text-slate-500">Aucune donnée</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
